import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import EcoPageHeader from "../../../components/eco-page-header/EcoPageHeader";
import type { RootState } from "../../../store/store";
import { isDpFromToken } from "../../../utils/authToken";

import {
  DpSensitiveAccessLog,
  LEAVE_TYPE_LABELS,
  listSensitiveAccessLogsApi,
  SENSITIVE_RESOURCE_LABELS,
} from "../api/leavesApi";
import { formatDateOnly } from "../utils/format";
import { DP_LEAVE_PAGE, DP_LEAVES_PAGE, DP_PAGE } from "../routes";
import { useGoBack } from "../../../hooks/useGoBack";
import "./Dp.css";
import "./DpEco.css";

const LIMIT = 20;

const formatDateTime = (value: string) =>
  new Date(value).toLocaleString("pt-BR", { timeZone: "America/Sao_Paulo", dateStyle: "short", timeStyle: "short" });

/**
 * Quem acessou dado de saúde de quem, e quando (ADR 0006 — transparência exigida
 * pela LGPD). Só o papel DP abre; o ADMIN recebe 403 do backend.
 */
export default function SensitiveAccessLogsPage() {
  const navigate = useNavigate();
  const goBack = useGoBack(DP_LEAVES_PAGE());
  const token = useSelector((state: RootState) => state.auth.accessToken);
  const isDp = isDpFromToken(token);

  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [page, setPage] = useState(1);
  const [items, setItems] = useState<DpSensitiveAccessLog[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!token || !isDp) return;
    setLoading(true);
    try {
      const result = await listSensitiveAccessLogsApi(token, {
        page,
        limit: LIMIT,
        from: from || undefined,
        to: to || undefined,
      });
      setItems(result.items);
      setTotal(result.total);
    } catch (error: any) {
      toast.error(error?.message || "Erro ao carregar os acessos.");
    } finally {
      setLoading(false);
    }
  }, [token, isDp, page, from, to]);

  useEffect(() => {
    load();
  }, [load]);

  const totalPages = Math.max(1, Math.ceil(total / LIMIT));

  if (!token) return null;

  return (
    <div className="dpLayout">
      <main className="dpMain">
        <div className="dpContainer">
          <EcoPageHeader
            crumbs={[
              { label: "Departamento Pessoal", onClick: () => navigate(DP_PAGE()) },
              { label: "Afastamentos", onClick: () => navigate(DP_LEAVES_PAGE()) },
              { label: "Acessos a dados de saúde" },
            ]}
            title="Acessos a dados de saúde"
            subtitle="Cada visualização de CID/médico e cada download de atestado ou CAT. O registro não pode ser alterado nem apagado."
            onBack={goBack}
            backLabel="Voltar para Afastamentos"
          />

          {!isDp ? (
            <p className="dpNotice dpNotice--warning">Somente o papel DP acessa este registro.</p>
          ) : (
            <>
              <div className="dpControls">
                <input
                  type="date"
                  className="dpInput"
                  aria-label="De"
                  value={from}
                  onChange={(e) => {
                    setPage(1);
                    setFrom(e.target.value);
                  }}
                />
                <input
                  type="date"
                  className="dpInput"
                  aria-label="Até"
                  value={to}
                  onChange={(e) => {
                    setPage(1);
                    setTo(e.target.value);
                  }}
                />
              </div>

              <div className="dpTableWrapper">
                <table className="dpTable">
                  <thead>
                    <tr>
                      <th>Quando</th>
                      <th>Quem acessou</th>
                      <th>Colaborador</th>
                      <th>O quê</th>
                      <th>IP</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan={5} className="dpEmpty">
                          Carregando...
                        </td>
                      </tr>
                    ) : items.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="dpEmpty">
                          Nenhum acesso registrado no período.
                        </td>
                      </tr>
                    ) : (
                      items.map((log) => (
                        <tr key={log.id} style={{ cursor: "pointer" }} onClick={() => navigate(DP_LEAVE_PAGE(log.leaveId))}>
                          <td>{formatDateTime(log.accessedAt)}</td>
                          <td>{log.user.nomeCompleto}</td>
                          <td>
                            <strong>{log.employee.fullName}</strong>
                            <div className="dpMuted">
                              {LEAVE_TYPE_LABELS[log.leave.type]} de {formatDateOnly(log.leave.startDate)}
                            </div>
                          </td>
                          <td>
                            {SENSITIVE_RESOURCE_LABELS[log.resource]} · {log.action === "VIEW" ? "visualização" : "download"}
                          </td>
                          <td>{log.ip ?? "—"}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              <div className="dpPagination">
                <span>
                  Mostrando {items.length} de {total} acessos
                </span>
                <div className="dpPagination__btns">
                  <button
                    className="dpBtn dpBtn--secondary"
                    style={{ height: 32 }}
                    disabled={page <= 1 || loading}
                    onClick={() => setPage((p) => p - 1)}
                  >
                    Anterior
                  </button>
                  <span style={{ padding: "0 12px" }}>
                    Página {page} de {totalPages}
                  </span>
                  <button
                    className="dpBtn dpBtn--secondary"
                    style={{ height: 32 }}
                    disabled={page >= totalPages || loading}
                    onClick={() => setPage((p) => p + 1)}
                  >
                    Próxima
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
